"use client";

import type { StaticImageData } from "next/image";
import type { Locale } from "@mw/shared/i18n";
import { useCurrency } from "@/app/_providers/currency";
import { getPriceForCurrency } from "@mw/shared/pricing";
import { formatPrice } from "@utilities/numbers";
import { useLocale } from "next-intl";
import { EventName } from "@mw/shared/analyticsEventNames";

import { DottedBorder } from "../core/GradientBorder";
import { LinkTracked } from "../core/LinkTracked";
import { usePricingTableContext } from "./PricingTableContext";
import { getModelPrice } from "./models-pricing-data";
import { PricingCardHeader } from "./PricingCardHeader";
import { PricingCardLayout } from "./PricingCardLayout";

/** Translated model data passed from server component */
export interface TranslatedModelData {
  id: string;
  name: string;
  category: string;
  priceUnit: string;
  features: string[];
  coverImage?: StaticImageData;
  ctaLink?: string;
  ctaLabel?: string;
}

interface ModelCardProps {
  model: TranslatedModelData;
  /** Whether this card is highlighted (shows cover image and white text) */
  highlighted?: boolean;
  /** Callback when hover starts on this card */
  onHoverStart?: () => void;
}

export const ModelCard = ({ model, highlighted = false, onHoverStart }: ModelCardProps) => {
  const locale = useLocale() as Locale;
  const { currency, currencySymbol } = useCurrency();
  const { billingCycle, featureFlags } = usePricingTableContext();

  // Get the price dynamically based on billing cycle (reuses API pricing table logic)
  const priceUsd = getModelPrice(model.id, billingCycle === "yearly" ? "yearly" : "monthly", { featureFlags });
  const convertedPrice = getPriceForCurrency(priceUsd, currency);
  const formattedPrice = formatPrice(convertedPrice, currencySymbol, locale);

  return (
    <PricingCardLayout
      onHoverStart={onHoverStart}
      header={
        <PricingCardHeader
          title={model.name}
          subtitle={
            <p className={`tw-opacity-80 tw-transition-colors tw-type-xs ${highlighted ? "tw-text-white" : ""}`}>
              {model.category}
            </p>
          }
          price={formattedPrice}
          priceUnit={model.priceUnit}
          priceUnitPlacement='below'
          coverImage={model.coverImage}
          backgroundColor='cream'
          highlighted={highlighted}
        />
      }
      cta={
        model.ctaLink && model.ctaLabel ? (
          <LinkTracked
            eventName={EventName.mw_model_card_cta_clicked}
            eventProperties={{
              model_id: model.id,
              model_name: model.name,
            }}
            asButton
            buttonVariants={{
              variant: "primary",
              size: "lg",
              expand: true,
            }}
            href={model.ctaLink}
            label={model.ctaLabel}
          />
        ) : undefined
      }
      features={
        <ul className='tw-pb-4'>
          {model.features.map((feature, index) => (
            <li key={index}>
              <div className='tw-py-3'>
                <span className='tw-type-xs tw-leading-xs'>{feature}</span>
              </div>
              {index < model.features.length - 1 && <DottedBorder side='inline' />}
            </li>
          ))}
        </ul>
      }
    />
  );
};
